/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import * as z from "zod/v4-mini";
import { remap as remap$ } from "../../lib/primitives.js";
import { ClosedEnum } from "../../types/enums.js";
import {
  AnalyticsFilter,
  AnalyticsFilter$Outbound,
  AnalyticsFilter$outboundSchema,
} from "./analyticsfilter.js";
import {
  AnalyticsMeasure,
  AnalyticsMeasure$Outbound,
  AnalyticsMeasure$outboundSchema,
} from "./analyticsmeasure.js";
import {
  AnalyticsOrderBy,
  AnalyticsOrderBy$Outbound,
  AnalyticsOrderBy$outboundSchema,
} from "./analyticsorderby.js";

/**
 * Time bucket width for a grouped query. Absent means none.
 */
export const Grain = {
  None: "none",
  Hour: "hour",
  Day: "day",
  Week: "week",
  Month: "month",
} as const;
/**
 * Time bucket width for a grouped query. Absent means none.
 */
export type Grain = ClosedEnum<typeof Grain>;

/**
 * A query against one dataset, in catalog vocabulary. Organization comes from the session and project from the project header; neither is a field.
 */
export type AnalyticsQueryPayload = {
  /**
   * Dataset to query, per describe
   */
  dataset: string;
  /**
   * Group-by key when grouped; projected columns when ungrouped. At most 3.
   */
  dimensions?: Array<string> | undefined;
  /**
   * Filters, ANDed. At most 100 values per filter.
   */
  filters?: Array<AnalyticsFilter> | undefined;
  /**
   * Start of the half-open window [from, to), ISO 8601
   */
  from: Date;
  /**
   * Time bucket width for a grouped query. Absent means none.
   */
  grain?: Grain | undefined;
  /**
   * Maximum rows. Defaults to 100; at most 1000 for a grouped result and 200 for ungrouped rows.
   */
  limit?: number | undefined;
  /**
   * Composed measures. Required when grouped, forbidden when ungrouped.
   */
  measures?: Array<AnalyticsMeasure> | undefined;
  /**
   * Sort for a grouped result, by measure alias. Ungrouped rows are always newest first.
   */
  orderBy?: Array<AnalyticsOrderBy> | undefined;
  /**
   * End of the half-open window [from, to), ISO 8601
   */
  to: Date;
  /**
   * Return rows at the dataset's grain instead of aggregating.
   */
  ungrouped?: boolean | undefined;
};

/** @internal */
export const Grain$outboundSchema: z.ZodMiniEnum<typeof Grain> = z.enum(Grain);

/** @internal */
export type AnalyticsQueryPayload$Outbound = {
  dataset: string;
  dimensions?: Array<string> | undefined;
  filters?: Array<AnalyticsFilter$Outbound> | undefined;
  from: string;
  grain?: string | undefined;
  limit: number;
  measures?: Array<AnalyticsMeasure$Outbound> | undefined;
  order_by?: Array<AnalyticsOrderBy$Outbound> | undefined;
  to: string;
  ungrouped: boolean;
};

/** @internal */
export const AnalyticsQueryPayload$outboundSchema: z.ZodMiniType<
  AnalyticsQueryPayload$Outbound,
  AnalyticsQueryPayload
> = z.pipe(
  z.object({
    dataset: z.string(),
    dimensions: z.optional(z.array(z.string())),
    filters: z.optional(z.array(AnalyticsFilter$outboundSchema)),
    from: z.pipe(z.date(), z.transform(v => v.toISOString())),
    grain: z.optional(Grain$outboundSchema),
    limit: z._default(z.int(), 100),
    measures: z.optional(z.array(AnalyticsMeasure$outboundSchema)),
    orderBy: z.optional(z.array(AnalyticsOrderBy$outboundSchema)),
    to: z.pipe(z.date(), z.transform(v => v.toISOString())),
    ungrouped: z._default(z.boolean(), false),
  }),
  z.transform((v) => {
    return remap$(v, {
      orderBy: "order_by",
    });
  }),
);

export function analyticsQueryPayloadToJSON(
  analyticsQueryPayload: AnalyticsQueryPayload,
): string {
  return JSON.stringify(
    AnalyticsQueryPayload$outboundSchema.parse(analyticsQueryPayload),
  );
}
